import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import ReactModal from 'react-modal';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box, Flex } from '@rover/kibble/core';
import Overlay from '@rover/kibble/official-patterns/Modal/Overlay';
import useTheme from '@rover/kibble/themes/useTheme';
import { useModalOpenTrackerEffect } from '@rover/kibble/utils/modalOpenTracker';

import { SpacingSystemType } from '../../core/styledSystemPropTypes';
import { usePreventScroll, useReactModalSetup } from '../Modal/Modal.common';

import { BottomSheetCommonProps, defaultContentPaddingStyles } from './BottomSheet.common';
import { DismissButton } from './DismissButton';

export const CLOSE_TIMEOUT = 300;

const overlayElement = (
  props: React.ComponentPropsWithRef<'div'>,
  contentEl: React.ReactNode
): JSX.Element => (
  <Overlay data-testid="overlay" {...props}>
    {contentEl}
  </Overlay>
);

/** Props for web BottomSheet component */
export type Props = BottomSheetCommonProps & {
  /** Allow dismissing by clicking overlay or pressing escape */
  isDismissible?: boolean;
  /** Use full screen height (web-only) */
  isFullHeight?: boolean;
  /** Custom ReactModal styles */
  style?: ReactModal.Styles;
  /** ID for header element for aria-labelledby */
  headerId?: string;
  /** Optional header content at top of sheet */
  HeaderComponent?: React.ReactNode;
  /** Optional footer content at bottom of sheet */
  FooterComponent?: React.ReactNode;
  /** Selector for app root element for ReactModal */
  appElementSelector?: string;
  /** Vertical overflow behavior */
  overflowY?: 'auto' | 'hidden';
  /** Animation direction for full-height sheets: 'vertical' (bottom-up) or 'horizontal' (right-left) */
  animationDirection?: 'vertical' | 'horizontal';
  /** Height of sheet (RxN-only, web fits to content) */
  height?: number | number[];
};

/**
 * BottomSheet displays content in a modal that slides up from the bottom of the screen.
 * Supports drag-to-dismiss, full-height mode, and custom headers/footers.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/4147905031/BottomSheet
 */
const BottomSheet = ({
  isOpen,
  isDismissible = true,
  closeOnDragDown = false,
  isFullHeight,
  onRequestClose,
  children,
  style,
  headerId,
  HeaderComponent,
  FooterComponent,
  appElementSelector = '#base-content',
  hideCloseIcon = false,
  overflowY,
  contentPaddingStyles = defaultContentPaddingStyles,
  animationDirection: _animationDirection = 'vertical',
}: Props): JSX.Element => {
  const theme = useTheme();
  const { colors } = theme;

  const { staticHeaderId, modalId } = useReactModalSetup(appElementSelector);
  usePreventScroll(isOpen, modalId);
  useModalOpenTrackerEffect(isOpen);

  const [isDragging, setIsDragging] = useState(false);
  const [initialY, setInitialY] = useState(null);
  const [currentY, setCurrentY] = useState(0);

  /** The effective animation direction, as `'horizontal'` would not make sense with a non-full height BottomSheet */
  const appliedAnimationDirection = isFullHeight ? _animationDirection : 'vertical';
  const transformOpen =
    appliedAnimationDirection === 'horizontal' ? 'translateX(0)' : 'translateY(0)';
  const transformClosed =
    appliedAnimationDirection === 'horizontal' ? 'translateX(100%)' : 'translateY(100%)';
  const [transform, setTransform] = useState<CSSProperties['transform']>(
    isOpen ? transformOpen : transformClosed
  );

  // BottomSheet first renders with `isOpen=true`, but we want it to start offscreen so it slides in smoothly,
  // so this offsets the transform state by one frame
  useEffect(() => {
    if (isOpen) {
      setTransform(transformOpen);
    } else {
      setTransform(transformClosed);
    }
  }, [isOpen, transformClosed, transformOpen]);

  const startDrag = (e) => {
    setIsDragging(true);
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    setInitialY(clientY);
  };

  const onDrag = useCallback(
    (e) => {
      if (!isDragging) return;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const deltaY = clientY - (initialY ?? clientY);
      setCurrentY(Math.max(deltaY, 0)); // Prevent dragging up beyond initial position
    },
    [isDragging, initialY]
  );

  const endDrag = useCallback(() => {
    setIsDragging(false);
    if (currentY > 75) {
      // Threshold to close the sheet
      onRequestClose();
    } else {
      setCurrentY(0); // Snap back to the original position
    }
  }, [currentY, onRequestClose]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', onDrag);
      document.addEventListener('mouseup', endDrag);
      document.addEventListener('touchmove', onDrag);
      document.addEventListener('touchend', endDrag);
    } else {
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchmove', onDrag);
      document.removeEventListener('touchend', endDrag);
    }

    return () => {
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchmove', onDrag);
      document.removeEventListener('touchend', endDrag);
    };
  }, [isDragging, endDrag, onDrag]);

  useEffect(() => {
    if (isOpen) {
      setCurrentY(0);
    }
  }, [isOpen]);

  let paddingTop: SpacingSystemType = '8x';
  if (closeOnDragDown) {
    paddingTop = '0x';
  } else if (isFullHeight) {
    paddingTop = '4x';
  }

  const transition =
    appliedAnimationDirection === 'horizontal'
      ? 'transform 0.4s cubic-bezier(0, 0.78, 0, 0.99), opacity 0.3s'
      : 'transform 0.3s, opacity 0.3s';

  return (
    <ReactModal
      aria={{ labelledby: headerId || staticHeaderId }}
      testId="bottom-sheet"
      className="RoverModalContent"
      style={{
        content: {
          width: '100%',
          ...(isFullHeight
            ? {
                height: '100%',
              }
            : {
                position: 'absolute',
                bottom: `-${currentY}px`,
                left: 0,
                right: 0,
              }),
          transform,
          opacity: isOpen ? 1 : 0 /* Start with opacity 0 */,
          transition,
          ...(style?.content && { ...style.content }),
        },
        overlay: {
          padding: 0,
          ...(style?.overlay && { ...style.overlay }),
        },
      }}
      closeTimeoutMS={CLOSE_TIMEOUT}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="RoverModalOverlay"
      overlayElement={overlayElement}
      shouldCloseOnOverlayClick={isDismissible}
      shouldCloseOnEsc={isDismissible}
    >
      <Flex
        background="primary"
        flexDirection="column"
        {...(isFullHeight
          ? {
              height: '100%',
              overflowY: 'auto',
            }
          : {
              borderRadius: 'primary',
              borderBottomLeftRadius: '0',
              borderBottomRightRadius: '0',
            })}
        minHeight="100px"
        paddingTop={paddingTop}
        paddingBottom="6x"
        zIndex={ZIndex.MODAL.toString()}
      >
        {closeOnDragDown && (
          <Flex
            alignItems="center"
            width="100%"
            height="48px"
            display="flex"
            justifyContent="center"
            webProps={{
              onMouseMove: onDrag,
              onTouchMove: onDrag,
              onMouseUp: endDrag,
              onTouchEnd: endDrag,
              onMouseDown: startDrag,
              onTouchStart: startDrag,
            }}
          >
            <Flex
              width="50px"
              height="5px"
              backgroundColor={colors.border.primary.toString()}
              borderRadius="primary"
              webProps={{
                tabIndex: 0,
                onKeyDown: (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    onRequestClose();
                  }
                },
              }}
              onClick={onRequestClose}
              data-testid="modal-dismiss-button"
              aria-label="Dismiss modal"
              role="button"
            />
          </Flex>
        )}
        <Flex flexDirection="column" height="100%" {...(!isFullHeight && { maxHeight: '80vh' })}>
          {((isDismissible && !closeOnDragDown && !hideCloseIcon) || HeaderComponent) && (
            <Flex
              mb="4x"
              alignItems="center"
              justifyContent={HeaderComponent ? 'space-between' : 'flex-end'}
              {...contentPaddingStyles}
            >
              <Box>{HeaderComponent}</Box>
              <DismissButton
                isDismissible={isDismissible}
                hideCloseIcon={hideCloseIcon}
                closeOnDragDown={closeOnDragDown}
                onRequestClose={onRequestClose}
              />
            </Flex>
          )}
          <Box {...contentPaddingStyles} sx={{ overflowY }} height={isFullHeight ? '100%' : 'auto'}>
            {children}
          </Box>
          {FooterComponent && <Box {...contentPaddingStyles}>{FooterComponent}</Box>}
        </Flex>
      </Flex>
    </ReactModal>
  );
};

export default BottomSheet;
